import { motion } from 'framer-motion';
import {
    ArrowRight,
    CheckCircle,
    Code2,
    Shield,
    Wallet
} from 'lucide-react';
import { Link } from 'react-router-dom';

const IDWallet = () => {
    return (
        <div className="min-h-screen pt-20">
            {/* Hero Section */}
            <section className="py-20 bg-surface-100">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                            Standards-Based ID Wallet
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            Launch your own branded digital identity wallet with our white-label solution or integrate wallet functionality directly into your existing applications.
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-3 gap-8 mb-16">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="glass rounded-xl p-6 border border-white/20"
                        >
                            <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-4">
                                <Wallet className="w-6 h-6 text-blue-400" />
                            </div>
                            <h2 className="text-xl font-bold text-white mb-3">White-Label Wallet</h2>
                            <p className="text-gray-300 mb-4">Complete branded wallet solution ready for iOS and Android deployment with your organization&apos;s identity.</p>
                            <ul className="space-y-2 text-sm text-gray-300">
                                <li className="flex items-center space-x-2">
                                    <CheckCircle className="w-4 h-4 text-green-400" />
                                    <span>Custom branding & theming</span>
                                </li>
                                <li className="flex items-center space-x-2">
                                    <CheckCircle className="w-4 h-4 text-green-400" />
                                    <span>Native iOS & Android apps</span>
                                </li>
                                <li className="flex items-center space-x-2">
                                    <CheckCircle className="w-4 h-4 text-green-400" />
                                    <span>Enterprise security features</span>
                                </li>
                            </ul>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            className="glass rounded-xl p-6 border border-white/20"
                        >
                            <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center mb-4">
                                <Code2 className="w-6 h-6 text-green-400" />
                            </div>
                            <h2 className="text-xl font-bold text-white mb-3">Wallet SDK</h2>
                            <p className="text-gray-300 mb-4">Integrate wallet functionality into your existing applications with our comprehensive SDK library.</p>
                            <ul className="space-y-2 text-sm text-gray-300">
                                <li className="flex items-center space-x-2">
                                    <CheckCircle className="w-4 h-4 text-green-400" />
                                    <span>React Native & Flutter</span>
                                </li>
                                <li className="flex items-center space-x-2">
                                    <CheckCircle className="w-4 h-4 text-green-400" />
                                    <span>Web JavaScript SDK</span>
                                </li>
                                <li className="flex items-center space-x-2">
                                    <CheckCircle className="w-4 h-4 text-green-400" />
                                    <span>RESTful API integration</span>
                                </li>
                            </ul>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                            className="glass rounded-xl p-6 border border-white/20"
                        >
                            <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center mb-4">
                                <Shield className="w-6 h-6 text-purple-400" />
                            </div>
                            <h2 className="text-xl font-bold text-white mb-3">Enterprise Features</h2>
                            <p className="text-gray-300 mb-4">Advanced security and management features designed for enterprise-scale deployments.</p>
                            <ul className="space-y-2 text-sm text-gray-300">
                                <li className="flex items-center space-x-2">
                                    <CheckCircle className="w-4 h-4 text-green-400" />
                                    <span>Biometric authentication</span>
                                </li>
                                <li className="flex items-center space-x-2">
                                    <CheckCircle className="w-4 h-4 text-green-400" />
                                    <span>Multi-signature support</span>
                                </li>
                                <li className="flex items-center space-x-2">
                                    <CheckCircle className="w-4 h-4 text-green-400" />
                                    <span>Audit & compliance tools</span>
                                </li>
                            </ul>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="py-20 bg-gradient-to-r from-primary-600 to-neon-600">
                <div className="container-custom text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                            Ready to Launch Your Digital Wallet?
                        </h2>
                        <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                            Get started with our white-label solution or integrate our SDK into your existing platform.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a href="https://app.passhub.documint.id/signup" target="_blank" rel="noopener noreferrer" className="bg-white text-primary-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center">
                                Start Free Trial
                                <ArrowRight size={20} className="ml-2" />
                            </a>
                            <Link to="/contact" className="border border-white/20 text-white hover:bg-white/10 px-8 py-3 rounded-lg font-semibold transition-colors duration-200 inline-flex items-center">
                                Contact Sales
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default IDWallet;